import React, { useMemo } from "react";
import {
	Autocomplete,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	TextField,
	Typography,
} from "@mui/material";

const normalizeSearchText = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const DelegacionMultiSelect = ({
	delegaciones = [],
	value = [],
	onChange = () => {},
	label = "Delegaciones asignadas",
	disabled = false,
	helperText = "",
}) => {
	const delegacionesValidas = useMemo(
		() =>
			Array.isArray(delegaciones)
				? delegaciones.filter(
						(delegacion) =>
							delegacion &&
							Number.isFinite(Number(delegacion.id)) &&
							Number(delegacion.id) > 0
				  )
				: [],
		[delegaciones]
	);

	const selectedIds = useMemo(() => {
		if (!Array.isArray(value)) {
			return [];
		}

		return [
			...new Set(
				value
					.map((item) => {
						const id = typeof item === "object" && item !== null ? item.id : item;
						return Number(id);
					})
					.filter((id) => Number.isFinite(id) && id > 0)
			),
		];
	}, [value]);

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const selectedDelegaciones = useMemo(
		() =>
			delegacionesValidas.filter((delegacion) =>
				selectedSet.has(Number(delegacion.id))
			),
		[delegacionesValidas, selectedSet]
	);

	return (
		<FormControl fullWidth disabled={disabled}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				disabled={disabled}
				options={delegacionesValidas}
				value={selectedDelegaciones}
				isOptionEqualToValue={(option, selected) =>
					Number(option?.id) === Number(selected?.id)
				}
				getOptionLabel={(option) =>
					option?.nombre?.trim() || `Delegacion ${option?.id ?? ""}`
				}
				filterOptions={(options, state) => {
					const query = normalizeSearchText(state?.inputValue);

					if (!query) {
						return options;
					}

					return options.filter((option) => {
						const searchableText = normalizeSearchText(
							[option?.nombre, option?.id, option?.slug]
								.filter(
									(item) =>
										item !== undefined &&
										item !== null &&
										item !== ""
								)
								.join(" ")
						);

						return searchableText.includes(query);
					});
				}}
				onChange={(_, nextValue) => {
					const nextIds = [
						...new Set(
							nextValue
								.map((item) => Number(item?.id))
								.filter((id) => Number.isFinite(id) && id > 0)
						),
					];

					onChange(nextIds);
				}}
				renderOption={(props, option, { selected }) => (
					<li {...props} key={`delegacion-option-${option.id}`}>
						<Checkbox checked={selected} sx={{ mr: 1 }} />

						<div>
							<Typography variant="body1">
								{option?.nombre?.trim() || `Delegacion ${option.id}`}
							</Typography>

							{option?.slug ? (
								<Typography variant="caption" color="text.secondary">
									{option.slug}
								</Typography>
							) : null}
						</div>
					</li>
				)}
				renderTags={(selectedOptions, getTagProps) =>
					selectedOptions.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });

						return (
							<Chip
								{...tagProps}
								key={key || `delegacion-chip-${option.id}`}
								size="small"
								label={option?.nombre?.trim() || `Delegacion ${option.id}`}
							/>
						);
					})
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder={
							selectedDelegaciones.length === 0 ? "Buscar delegaciones..." : ""
						}
					/>
				)}
				noOptionsText="No hay delegaciones"
			/>

			{helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
		</FormControl>
	);
};

export default DelegacionMultiSelect;
